import {
  Grid,
  Typography,
  Box,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import tripService from "services/tripService";
import moment from "moment";
import { ExternalLogSource, OrderLogRequest } from "trips/models";
import MultiSelect from "common/components/MultiSelect";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { isTimelineLoading } from "store/vehicleTripSlice";

const EXTERNAL_SOURCE_OPTIONS: { id: ExternalLogSource; name: string }[] = [
  { id: "Logistrics", name: "Logistrics" },
  { id: "Uprr", name: "UPRR" },
  { id: "Dashboard", name: "Dashboard" },
  { id: "OrderApi", name: "OrderApi" },
  { id: "ClientPointGeoUpdate", name: "ClientPointGeoUpdate" },
  { id: "SqlAgent", name: "SQL Agent" },
  { id: "Ssrs", name: "SSRS" },
];

const TripAudit = ({ trip }) => {
  const initialSearch: OrderLogRequest = {
    offset: 0,
    pageSize: 25,
    sortAsc: false,
    sortBy: "logDateTime",
    tripId: trip?.id,
    includeTrace: false,
  };
  const [orderLogs, setOrderLogs] = useState([]);
  const [search, setSearch] = useState<OrderLogRequest>(initialSearch);
  const [selectedSources, setSelectedSources] = useState<{ id: ExternalLogSource; name: string }[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useGridApiRef();
  const timelineLoading = useSelector(isTimelineLoading);
  // Tracks the last fetched request so spurious setSearch calls (e.g. MUI
  // DataGrid firing onSortModelChange + onPaginationModelChange on mount with
  // values identical to initialState) don't refetch. Keyed on the payload —
  // any real user change to sort/page/filter produces a new key.
  const lastFetchKeyRef = useRef<string | null>(null);

  const fetchLogs = (req: OrderLogRequest) => {
    setIsLoading(true);
    setOrderLogs([]);
    setTotal(0);
    tripService
      .getOrderLogs(req)
      .then((results) => {
        setOrderLogs(results.results);
        setTotal(results.count);
      })
      .finally(() => setIsLoading(false));
  };

  // Single fetch path. Waits for the timeline to settle so server-side writes
  // triggered during the timeline call (e.g. auto-apply GPS) show up in logs.
  useEffect(() => {
    if (!trip?.id) return;
    if (timelineLoading) return;
    const req = { ...search, tripId: trip.id };
    const key = JSON.stringify(req);
    if (key === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = key;
    fetchLogs(req);
  }, [trip?.id, timelineLoading, search]);

  const columns: GridColDef[] = [
    { field: "action", headerName: "Action", width: 150, sortable:false },
    {
      field: "logSummary",
      headerName: "Summary",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, string>) => {
        const detail = params.row.logDetail;
        const full = (typeof detail === "string" ? (detail.startsWith("{") ? JSON.parse(detail).detail : detail) : detail?.detail) || params.value || "";
        const parts = full.split("; ");
        const summary = parts.length > 1 ? `${parts[0]} (+${parts.length - 1} more)` : full;
        return (
          <Tooltip title={full} arrow placement="top-start" slotProps={{ tooltip: { sx: { maxWidth: 600 } } }}>
            <Box
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                py: 0.5,
              }}
            >
              {summary}
            </Box>
          </Tooltip>
        );
      },
    },
    { field: "logSource", headerName: "Source", width: 150, sortable: false },
    { field: "createdBy", headerName: "Created By", width:170, sortable: false},
    {
      field: "logDateTime",
      headerName: "Log Time",
      width: 190,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>{`${params.value ? moment.utc(params.value).local().format("MM/DD/YYYY HH:mm") : ""}`}</>
      ),
    },
    ...(search.includeTrace
      ? [{ field: "logLevel", headerName: "Level", width: 90, sortable: false } as GridColDef]
      : []),
  ];

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      offset: 0,
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pageSize: pagination.pageSize, offset: pagination.page });
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(5)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="96%" sx={{ my: 1, ml: 2, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const clearFilters = () => {
    setSearch(initialSearch);
  };

  const handleIncludeTraceToggle = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSearch({ ...search, includeTrace: checked, offset: 0 });
    gridRef.current?.setPage(0);
  };

  const handleSourcesChange = (next: { id: ExternalLogSource; name: string }[]) => {
    setSelectedSources(next);
    const externalSources = next.map((s) => s.id);
    setSearch({
      ...search,
      externalSources: externalSources.length > 0 ? externalSources : undefined,
      offset: 0,
    });
    gridRef.current?.setPage(0);
  };

  return (
    <>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ ml: 1, mr: 2 }}
        >
          <Typography variant="body2">Order Logs</Typography>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: "wrap" }}>
            <MultiSelect
              label="External Sources"
              values={EXTERNAL_SOURCE_OPTIONS}
              selectedValues={selectedSources}
              setSelectedValues={handleSourcesChange}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!search.includeTrace}
                  onChange={handleIncludeTraceToggle}
                />
              }
              label={<Typography variant="caption">Include Trace</Typography>}
              labelPlacement="start"
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ mt: 2, overflow: "hidden" }}>
          <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", minHeight: 180, overflow: "hidden" }}>
            <DataGrid
              autoHeight={isLoading ? false : true}
              sx={{ overflow: "hidden", fontSize: ".95em", "--DataGrid-overlayHeight": "120px" }}
              rows={orderLogs || []}
              columns={columns}
              pagination
              sortingMode="server"
              paginationMode="server"
              onPaginationModelChange={handlePaginationModel}
              onSortModelChange={handleSortModelChange}
              initialState={{
                pagination: { paginationModel: { page: search.offset, pageSize: search.pageSize } },
                sorting: { sortModel: [{ field: "pickupTime", sort: "asc" }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={total}
              disableColumnMenu
              slots={{
                loadingOverlay: LoadingSkeleton,
                noRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    No Order Logs
                  </Stack>
                ),
              }}
              loading={isLoading}
              density="compact"
              apiRef={gridRef as any}
              getRowHeight={() => "auto"}
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
            />
          </Paper>
        </Box>
      </Grid>
    </>
  );
};
export default TripAudit;
